// =============================================================================
// adhesionService — chama a Cloud Function radarCreateAdhesion no cs-dgenny.
// =============================================================================
// Fluxo: cliente abre /registro-cliente?token=XXX → preenche etapas 1-3
// (dados empresa + responsáveis) → Etapa 4 mostra termo + checkbox → ao
// aceitar, este service envia tudo pro backend, que grava Adhesion +
// atualiza Radar do cs-dgenny. NÃO cria customer no Asaas/Conexa — o
// fluxo novo (com proposta Dgenny no token) substitui o submit antigo.
// =============================================================================

// URL fixa de produção da Cloud Function. Não troca por ambiente porque
// deal-plans hoje só roda em produção (Vercel) e o backend sempre é o
// `sistemas-internos-dgenny`. Se um dia houver staging, parametrizar via env.
const CLOUD_FUNCTION_URL =
  'https://us-central1-sistemas-internos-dgenny.cloudfunctions.net/radarCreateAdhesion';

export interface AdhesionContato {
  nome: string;
  cargo?: string;
  cpf?: string;
  email: string;
  telefone: string;
}

/** Valida WhatsApp BR (DDD + número, 10–11 dígitos; aceita +55). Espelha
 *  normalizePhoneBr do cs-dgenny — o servidor rejeita com 400 se inválido. */
export function isWhatsappBrValido(raw: string): boolean {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length < 10) return false;
  const full = digits.startsWith('55') && digits.length >= 12 ? digits : `55${digits}`;
  return full.length >= 12 && full.length <= 13;
}

/** Máscara de exibição (XX) XXXXX-XXXX / (XX) XXXX-XXXX. */
export function formatTelefoneBr(raw: string): string {
  const d = String(raw || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

// SignatarioAceite identifica QUEM clica em "Aceitar" na Etapa 4. No fluxo
// antigo era sempre o representante legal; no novo, é o operacional ou o
// financeiro (default operacional), podendo ser preenchido manualmente
// quando nenhum dos dois assina. `fonte` permite auditoria server-side.
export type SignatarioAceiteFonte = 'operacional' | 'financeiro' | 'manual';

export interface SignatarioAceite extends AdhesionContato {
  fonte: SignatarioAceiteFonte;
}

/**
 * Campos do `deal_terms` do token quando o link é de RECURSO ADICIONAL
 * (aditivo — `tipo_adesao === 'aditivo'`). Todos opcionais porque o token de
 * plano (fluxo principal) não os traz. Chaves iguais às gravadas pelo Radar.
 */
export interface TokenDealTermsAditivo {
  tipo_adesao?: 'plano' | 'aditivo';
  parent_adhesion_id?: string;
  parent_document_hash?: string;
  /** Já em DD/MM/AAAA. */
  parent_accepted_at?: string;
  produto_nome?: string;
  produto_descricao?: string;
  valor_mensalidade_centavos?: number;
  valor_implantacao_centavos?: number;
  /** YYYY-MM-DD. */
  data_inicio_prevista?: string;
  dia_vencimento?: number;
  pro_rata_primeira_mensalidade?: boolean;
  conta_separada?: boolean;
  condicoes_especiais?: string;
}

export interface AdhesionEmpresa {
  endereco: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
}

export interface CreateAdhesionPayload {
  token: string;
  empresa: AdhesionEmpresa;
  signatario_aceite: SignatarioAceite;
  operacional: AdhesionContato;
  financeiro: AdhesionContato;
  /** v1.5.1 — contatos além do líder operacional / financeiro principal. */
  operacionais_adicionais?: AdhesionContato[];
  financeiros_adicionais?: AdhesionContato[];
  /** Cliente quer também receber minuta de contrato formal por email após o aceite. */
  quer_contrato_formal: boolean;
  /**
   * Compat: backend antigo lê `representante` como signatário. Mantido aqui
   * como echo de `signatario_aceite` para não quebrar a migração — pode ser
   * removido quando o backend só ler `signatario_aceite`.
   */
  representante?: AdhesionContato;
}

export interface CreateAdhesionResult {
  ok: boolean;
  adhesionId: string;
  documentHash: string;
  acceptedAt: string;
  company: { id: string; razao_social: string; cnpj: string };
}

export async function createAdhesion(
  payload: CreateAdhesionPayload,
): Promise<CreateAdhesionResult> {
  const resp = await fetch(CLOUD_FUNCTION_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!resp.ok) {
    let message = `HTTP ${resp.status}`;
    try {
      const errData = await resp.json();
      message = errData?.error || message;
    } catch {
      // ignora — fica com HTTP code
    }
    throw new Error(message);
  }

  return (await resp.json()) as CreateAdhesionResult;
}

// =============================================================================
// Contrato Formal (opcional) — chamado quando o cliente marca "Quero contrato
// formal" no aceite da Etapa 4. Backend gera minuta e envia por email aos
// representantes legais, testemunha (se houver), operacional, financeiro e
// emails adicionais. Todos os contatos são vinculados à mesma `company.id`
// (grupo econômico) da Adhesion.
// =============================================================================

const CONTRACT_DRAFT_URL =
  'https://us-central1-sistemas-internos-dgenny.cloudfunctions.net/radarCreateContractDraft';

export interface CreateContractDraftPayload {
  /** ID da Adhesion já criada (Etapa 4). Backend resolve company.id por aqui. */
  adhesionId: string;
  /** Quem assina pela contratante. Mínimo 1. */
  representantes_legais: AdhesionContato[];
  /**
   * Testemunha 1 — da CONTRATANTE. Obrigatória pelo formato contrato (CPC
   * art. 784, III) para que vire título executivo extrajudicial. Nome e CPF
   * obrigatórios; email opcional (usado para envio da minuta).
   * Testemunha 2 é da DGENNY e é preenchida pelo backend com colaborador
   * padrão — não vem deste payload.
   * Ver: dgenny-contratos/contrato-completo/placeholders.md
   */
  testemunha?: { nome: string; cpf: string; email?: string };
  /** Emails extras (jurídico, etc) que devem receber cópia da minuta. */
  emails_adicionais: string[];
  /** Evita duplicação em caso de clique duplo / retry. UUID v4 gerado client-side. */
  idempotency_key: string;
}

export interface CreateContractDraftResult {
  ok: boolean;
  contractDraftId: string;
  emailsEnviados: number;
}

/**
 * Envia pedido de geração de minuta de contrato formal. Sempre chama o
 * backend real (radarCreateContractDraft): gera e persiste a minuta,
 * enfileira o email pros destinatários (BCC: vendedor) e notifica o Slack.
 * O modo stub por feature flag (`flags.contract_draft_live` do token) foi
 * removido em jul/2026 — descartava silenciosamente os dados do cliente.
 */
export async function requestContractDraft(
  payload: CreateContractDraftPayload,
): Promise<CreateContractDraftResult> {
  const resp = await fetch(CONTRACT_DRAFT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!resp.ok) {
    let message = `HTTP ${resp.status}`;
    try {
      const errData = await resp.json();
      message = errData?.error || message;
    } catch {
      // mantém HTTP code
    }
    throw new Error(message);
  }

  return (await resp.json()) as CreateContractDraftResult;
}

// =============================================================================
// IP do cliente — usado pra registrar evidência do aceite no servidor.
// O backend captura o IP do request (x-forwarded-for), mas mantemos uma
// função utilitária aqui caso queiramos exibir o IP previamente ao usuário
// (transparência) ou logar no analytics. Não é estritamente necessário pro
// fluxo de aceite (server-side é a fonte de verdade).
// =============================================================================
export async function fetchClientIp(): Promise<string | null> {
  try {
    const resp = await fetch('https://api.ipify.org?format=json', {
      signal: AbortSignal.timeout(3000),
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as { ip?: string };
    return data.ip || null;
  } catch {
    return null;
  }
}
